import { useStore } from 'hooks';
import { useMemo } from 'react';

/**
 * Retrieves and processes the offline status for various system components including gateways, BRUs, sensors, and devices.
 * Uses data from the topology management store to filter items and calculate whether each item is online.
 *
 * @return {Object} An object containing the offline status and total count for gateways, BRUs, sensors, and devices.
 * The structure of the returned object is:
 * - gateways: { total: number, offline: Array }
 * - brus: { total: number, offline: Array }
 * - sensors: { total: number, offline: Array }
 * - devices: { total: number, offline: Array }
 *   - 'total' represents the total number of items.
 *   - 'offline' is an array of items that are offline.
 */
const useSystemOfflineData = () => {
  const { topologyManagementStore, hardwareStatusStore } = useStore();

  const coolers = topologyManagementStore.getFilteredItemsByKey('cooler');
  const gateways = topologyManagementStore.getFilteredItemsByKey('gateway');
  const brus = topologyManagementStore.getFilteredItemsByKey('bru');
  const sensors = topologyManagementStore.getFilteredItemsByKey('sensor');

  const gatewayStatusList = useMemo(
    () =>
      gateways.map(gateway => {
        const cooler = coolers.find(({ id }) => id === gateway.cooler_id);
        const isOnline = !hardwareStatusStore.offlineGatewaysIds.includes(gateway.id);

        return { ...gateway, isOnline, cooler };
      }),
    [gateways, coolers, hardwareStatusStore.offlineGatewaysIds],
  );

  const sensorStatusList = useMemo(
    () =>
      sensors.map(sensor => {
        const { bru_id, _gateways_id } = sensor || {};
        const bru = brus.find(({ id }) => id === bru_id);
        const gateway = gateways.find(({ id }) => id === _gateways_id);
        const cooler = coolers.find(({ id }) => id === gateway?.cooler_id);

        const isOnline =
          !hardwareStatusStore.offlineGatewaysIds.includes(gateway.id) &&
          !hardwareStatusStore.offlineFlowSensorsIds.includes(sensor.id);

        return {
          ...sensor,
          isOnline,
          gateway,
          bru,
          gateway_bru_address: bru?.gateway_bru_address,
          gateway_name: gateway?.name,
          cooler,
        };
      }),
    [
      brus,
      gateways,
      sensors,
      coolers,
      hardwareStatusStore.offlineGatewaysIds,
      hardwareStatusStore.offlineFlowSensorsIds,
    ],
  );

  return {
    gateways: {
      total: gateways.length,
      offline: gatewayStatusList.filter(gateway => gateway.isOnline === false),
    },
    sensors: {
      total: sensors.length,
      offline: sensorStatusList.filter(sensor => sensor.isOnline === false),
    },
    devices: {
      total: gateways.length + sensors.length,
      offline: [...gatewayStatusList, ...sensorStatusList].filter(
        device => device.isOnline === false,
      ),
    },
  };
};

export default useSystemOfflineData;
